import { type FC, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { OrderType } from '@/api/order/models/OrderType';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { Pending } from '@/components/Pending';

import { useExistingOrder } from './hooks/useExistingOrder';

/**
 * The page the user is redirected to when they cancel the signing of an order.
 * This component will redirect to the correct trading page based on the order type.
 */
export const SigningCancelledPage: FC = () => {
  const organizationCmId = useOrganizationCmId();
  const { data } = useExistingOrder();
  const navigate = useNavigate();

  useEffect(() => {
    if (!data) {
      return;
    }

    const { order } = data;

    switch (order.orderType) {
      case OrderType.Subscription:
        navigate(
          `/${organizationCmId}/trading/subscription?orderId=${order.id}&signingCancelled=true`,
          {
            replace: true,
          },
        );
        break;

      case OrderType.Redemption:
        navigate(
          `/${organizationCmId}/trading/redemption?orderId=${order.id}&signingCancelled=true`,
          {
            replace: true,
          },
        );
        break;

      case OrderType.Switch:
        navigate(
          `/${organizationCmId}/trading/switch?orderId=${order.id}&signingCancelled=true`,
          {
            replace: true,
          },
        );
        break;

      case OrderType.Deposit:
        navigate(
          `/${organizationCmId}/discretionary/deposit?orderId=${order.id}&signingCancelled=true`,
          {
            replace: true,
          },
        );
        break;

      case OrderType.Withdrawal:
        navigate(
          `/${organizationCmId}/discretionary/withdrawal?orderId=${order.id}&signingCancelled=true`,
          {
            replace: true,
          },
        );
        break;

      case OrderType.Transfer:
        navigate(
          `/${organizationCmId}/discretionary/transfer?orderId=${order.id}&signingCancelled=true`,
          {
            replace: true,
          },
        );
        break;

      default:
        throw new Error(
          'Unknown order type received when returning from cancelling signing order.',
        );
    }
  }, [data]);

  return <Pending center />;
};
